import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { ApiError } from '../api/client';
import { listRondoSegments, createRondoSegment, updateRondoSegment, deleteRondoSegment, type RondoSegmentItem } from '../api/rondo';
import { ServiceBar } from '../shell/ServiceBar';
import { Button } from '../components/arc/button/button';
import { Dialog, DialogContent } from '../components/arc/dialog/dialog';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { Select } from '../components/arc/select/select';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { useFeather } from '../shell/useFeather';
import { confirmDialog } from '../shell/dialogs';
import './users-seznam.css';
import './segmentace.css';

const intFmt = (n: number) => n.toLocaleString('cs-CZ');
const COUNTRIES = ['Vše', 'CZ', 'SK', 'RO'] as const;
const PARTNERS = ['Sazka (Allwyn)', 'GrandWin', 'Lucky Bet', 'Magic Planet', 'MerkurXtip'];

function segBadge(color: string): CSSProperties {
  return {
    display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 11,
    background: color, color: '#fff', fontSize: '0.78rem', fontWeight: 750,
  };
}

export function SegmentacePage() {
  const [country, setCountry] = useState<string>('Vše');
  const [partner, setPartner] = useState('all');
  const [stav, setStav] = useState('all');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<RondoSegmentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RondoSegmentItem | 'new' | null>(null);
  const [detail, setDetail] = useState<RondoSegmentItem | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      listRondoSegments({
        search: query.trim() || undefined,
        country: country !== 'Vše' ? country : undefined,
        partner: partner !== 'all' ? partner : undefined,
        stav: stav !== 'all' ? stav : undefined,
      })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items);
          setTotal(res.total);
          setError(null);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof ApiError ? e.message : 'Načtení selhalo');
          setRows([]);
          setTotal(0);
        })
        .finally(() => !cancelled && setLoading(false));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [country, partner, stav, query, reloadKey]);

  useFeather([rows, editing, detail]);

  function reset() {
    setCountry('Vše');
    setPartner('all');
    setStav('all');
    setQuery('');
  }

  const activeCount = rows.filter((s) => s.isActive).length;
  const usersCount = rows.reduce((sum, s) => sum + (s.userCount ?? 0), 0);
  const rulesCount = rows.reduce((sum, s) => sum + s.ruleCount, 0);
  const tableRows = rows.map((row) => ({ ...row, detail: row.id }));
  type TableRow = (typeof tableRows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'name', label: 'Název', width: 220, render: (_, row) => <button type="button" className="rondo-segment-link" onClick={() => setDetail(row)}><span className="rondo-segment-avatar" style={{ backgroundColor: row.color }}>{row.name.slice(0, 2).toUpperCase()}</span><span><strong>{row.name}</strong><small>Segment #{row.id}</small></span></button> },
    { key: 'country', label: 'Země', width: 80, render: (_, row) => <span className="rondo-segment-country">{row.country}</span> },
    { key: 'userCount', label: 'Uživatelé', numeric: true, width: 115, render: (_, row) => row.userCount == null ? '—' : intFmt(row.userCount) },
    { key: 'ruleCount', label: 'Pravidla', width: 240, render: (_, row) => <span className="rondo-segment-rules"><strong>{row.ruleCount} {row.ruleCount === 1 ? 'pravidlo' : row.ruleCount < 5 ? 'pravidla' : 'pravidel'}</strong><small>{row.ruleSummary ?? 'Bez podmínek'}</small></span> },
    { key: 'priority', label: 'Priorita', numeric: true, width: 90 },
    { key: 'isActive', label: 'Stav', width: 110, render: (_, row) => <span className="rondo-segment-status" data-active={row.isActive}>{row.isActive ? 'Aktivní' : 'Neaktivní'}</span> },
    { key: 'inList', label: 'V seznamu', width: 105, render: (_, row) => row.inList ? 'Ano' : 'Ne' },
    { key: 'detail', label: '', width: 45, sortable: false, render: (_, row) => <button type="button" className="rondo-arc-user-detail" aria-label={`Detail segmentu ${row.name}`} onClick={() => setDetail(row)}><ChevronRight size={16} aria-hidden="true" /></button> },
  ];

  return (
    <section className="rondo-arc-users-list rondo-arc-segments p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">UŽIVATELÉ</div><h1>Segmentace</h1><p>Pravidla a skupiny uživatelů Ronda</p></div><Button size="sm" onClick={() => setEditing('new')}><Plus size={15} aria-hidden="true" /> Nový segment</Button></header>

      <div className="rondo-arc-users-list-metrics" aria-label="Souhrn segmentace">
        <MetricCard label="Segmentů" value={total} context="Podle aktuálního filtru" />
        <MetricCard label="Aktivních" value={activeCount} context="Načtené segmenty" />
        <MetricCard label="Zařazených uživatelů" value={usersCount} context="Součet načtených segmentů" />
        <MetricCard label="Pravidel" value={rulesCount} context="V načtených segmentech" />
      </div>

      <ArcTabs label="Trh" items={COUNTRIES.map((value) => ({ value, label: value }))} value={country} onChange={setCountry} className="rondo-segment-markets" />

      <ServiceBar
        selects={[
          { label: 'Partner', value: partner, onChange: setPartner, options: [
            { value: 'all', label: 'Všichni' },
            ...PARTNERS.map((p) => ({ value: p, label: p })),
            { value: '- bez partnera -', label: '- bez partnera -' } ] },
          { label: 'Stav', value: stav, onChange: setStav, options: [
            { value: 'all', label: 'Všechny' }, { value: 'active', label: 'Aktivní' }, { value: 'inactive', label: 'Neaktivní' },
            { value: 'hidden', label: 'Skryté v listu' }, { value: 'in_list', label: 'Viditelné v listu' } ] },
        ]}
        onReset={reset}
        search={query}
        onSearch={setQuery}
        searchPlaceholder="název / pravidlo…"
        variant="seg"
      />

      <div className="rondo-arc-users-list-table rondo-arc-segments-table">
        {error && <p role="alert" className="rondo-arc-users-error">{error}</p>}
        {loading ? <p className="rondo-arc-users-loading">Načítám segmenty…</p> : <SortableDataTable rows={tableRows} columns={columns} rowKey="id" caption="Segmenty uživatelů Ronda" emptyMessage="Žádný segment neodpovídá filtru." />}
      </div>

      <p className="rondo-segment-count">{intFmt(rows.length)} zobrazených segmentů{total > rows.length ? ` z ${intFmt(total)}` : ''}</p>

      {detail && (
        <SegmentDetailModal
          segment={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null); }}
        />
      )}
      {editing && (
        <SegmentEditModal
          segment={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }}
        />
      )}
    </section>
  );
}

function ModalShell({ title, maxWidth, onClose, footer, children }: {
  title: string; maxWidth: number; onClose: () => void; footer: ReactNode; children: ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={title} description="Segmentace uživatelů" className="rondo-segment-dialog" style={{ maxWidth }}>
        <div className="rondo-segment-dialog-body p1-arc-scope">{children}</div>
        <div className="rondo-segment-dialog-actions p1-arc-scope">{footer}</div>
      </DialogContent>
    </Dialog>
  );
}

function SegmentDetailModal({ segment: s, onClose, onEdit }: {
  segment: RondoSegmentItem; onClose: () => void; onEdit: () => void;
}) {
  useFeather([s]);
  const check = (v: boolean) =>
    v ? <span className="text-success">✓</span> : <span className="text-danger">✗</span>;
  const ruleWord = s.ruleCount === 1 ? 'pravidlo' : s.ruleCount < 5 ? 'pravidla' : 'pravidel';
  return (
    <ModalShell
      title="Detail segmentu"
      maxWidth={560}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onEdit}>Upravit</Button>
          <Button variant="secondary" size="sm" onClick={onClose}>Zavřít</Button>
        </>
      }
    >
      <div className="segment-detail-hero"><span style={segBadge(s.color)}>{s.name.slice(0, 2).toUpperCase()}</span><div><h3>{s.name}</h3><p>{s.country} · Segment #{s.id}</p></div><span className={`segment-state ${s.isActive ? 'active' : ''}`}>{s.isActive ? 'Aktivní' : 'Neaktivní'}</span></div>
      <div className="segment-detail-stats"><article><small>Uživatelů</small><strong>{s.userCount == null ? '-' : intFmt(s.userCount)}</strong></article><article><small>Priorita</small><strong>{s.priority}</strong></article><article><small>V seznamu</small><strong>{check(s.inList)} {s.inList ? 'Ano' : 'Ne'}</strong></article></div>
      <section className="segment-rule-summary"><span>PRAVIDLA SEGMENTU</span><h4>{s.ruleCount} {ruleWord}</h4><p>{s.ruleSummary ?? 'Segment zatím nemá žádné podmínky.'}</p></section>
    </ModalShell>
  );
}

const SEG_COUNTRIES = ['CZ', 'SK', 'RO'] as const;
const RULE_METRICS: { code: string; label: string }[] = [
  { code: 'spin_count_30d', label: 'Spiny (30 dní)' },
  { code: 'tipovacka_bet_count_30d', label: 'Tipovačka sázky (30 dní)' },
  { code: 'tipovacka_bet_count_total', label: 'Tipovačka sázky celkem' },
  { code: 'survey_lead_count', label: 'Survey leady' },
  { code: 'super_hrac_score', label: 'Superhráč skóre' },
  { code: 'rondons_balance', label: 'RondoNy' },
  { code: 'cash_balance', label: 'Cash' },
  { code: 'days_since_active', label: 'Dní od aktivity' },
  { code: 'days_since_bet', label: 'Dní od sázky' },
  { code: 'account_age_days', label: 'Stáří účtu (dní)' },
  { code: 'inactive', label: 'Neaktivní' },
  { code: 'lead_partner', label: 'Lead partner (dokončený)' },
  { code: 'lead_partner_open', label: 'Lead partner (otevřený)' },
  { code: 'test_mail', label: 'Test mail' },
  { code: 'super_hrac', label: 'Superhráč' },
  { code: 'super_hrac_top', label: 'TOP hráč (CSV ∪ skóre≥60)' },
  { code: 'name_day', label: 'Svátek' },
  { code: 'birthday', label: 'Narozeniny' },
  { code: 'loyalty_tier', label: 'Věrnostní tier (Bronze/Silver/Gold/Platinum)' },
];
const RULE_OPERATORS: { code: string; label: string }[] = [
  { code: '>=', label: '≥' }, { code: '<=', label: '≤' }, { code: '>', label: '>' },
  { code: '<', label: '<' }, { code: '=', label: '=' }, { code: 'has', label: 'má' }, { code: 'not_has', label: 'nemá' },
];

interface DraftRule {
  metric: string;
  operator: string;
  value: string;
}

function SegmentEditModal({ segment, onClose, onSaved }: { segment: RondoSegmentItem | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = segment !== null;
  const [name, setName] = useState(segment?.name ?? '');
  const [country, setCountry] = useState<string>(segment?.country ?? 'CZ');
  const [color, setColor] = useState(segment?.color ?? '#007d88');
  const [priority, setPriority] = useState<number>(segment?.priority ?? 100);
  const [isActive, setIsActive] = useState<boolean>(segment?.isActive ?? true);
  const [inList, setInList] = useState<boolean>(segment?.inList ?? true);
  const [rules, setRules] = useState<DraftRule[]>(
    (segment?.rules ?? []).map((r) => ({ metric: r.metric, operator: r.op, value: String(r.value ?? '') })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useFeather([rules]);

  function addRule() {
    setRules((r) => [...r, { metric: RULE_METRICS[0]!.code, operator: RULE_OPERATORS[0]!.code, value: '' }]);
  }
  function updateRule(i: number, patch: Partial<DraftRule>) {
    setRules((r) => r.map((rule, idx) => (idx === i ? { ...rule, ...patch } : rule)));
  }
  function removeRule(i: number) {
    setRules((r) => r.filter((_, idx) => idx !== i));
  }

  async function save() {
    if (name.trim().length < 2) { setErr('Název musí mít aspoň 2 znaky.'); return; }
    setBusy(true); setErr(null);
    const input = {
      name: name.trim(), country, color, priority, active: isActive, inList,
      rules: rules.filter((r) => r.metric && r.operator).map((r) => ({ metric: r.metric, op: r.operator, value: r.value })),
    };
    try {
      if (isEdit && segment) await updateRondoSegment(segment.id, input);
      else await createRondoSegment(input);
      onSaved();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Uložení se nezdařilo.'); }
    finally { setBusy(false); }
  }
  async function del() {
    if (!isEdit || !segment) return;
    if (!(await confirmDialog({ title: 'Smazat segment?', message: `Segment „${segment.name}“ se smaže.`, confirmLabel: 'Smazat', danger: true }))) return;
    setBusy(true); setErr(null);
    try { await deleteRondoSegment(segment.id); onSaved(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Smazání se nezdařilo.'); }
    finally { setBusy(false); }
  }

  return (
    <ModalShell
      title={isEdit ? 'Upravit segment' : 'Nový segment'}
      maxWidth={680}
      onClose={onClose}
      footer={
        <>
          {isEdit && <Button variant="danger" size="sm" disabled={busy} onClick={del}>Smazat segment</Button>}
          <Button variant="secondary" size="sm" disabled={busy} onClick={onClose}>Zavřít</Button>
          <Button size="sm" loading={busy} onClick={save}>{busy ? 'Ukládám…' : 'Uložit'}</Button>
        </>
      }
    >
      {err && <div className="alert alert-danger py-2">{err}</div>}
      <div className="segment-editor-fields row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fs-8 text-muted">Název</label>
          <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-md-3">
          <label className="form-label fs-8 text-muted">Země</label>
          <Select label="Země" fieldClassName="rondo-segment-editor-select" value={country} onValueChange={setCountry} options={SEG_COUNTRIES.map((c) => ({ value: c, label: c }))} />
        </div>
        <div className="col-md-3">
          <label className="form-label fs-8 text-muted">Priorita</label>
          <input type="number" className="form-control form-control-sm" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </div>
        <div className="col-md-6">
          <label className="form-label fs-8 text-muted">Barva</label>
          <div className="d-flex align-items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 40, height: 31, padding: 2, border: '1px solid #eef0f3', borderRadius: 6 }} />
            <input className="form-control form-control-sm" value={color} onChange={(e) => setColor(e.target.value)} style={{ maxWidth: 120 }} />
          </div>
        </div>
        <div className="col-md-6 d-flex flex-column justify-content-end gap-1">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="seg-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label className="form-check-label" htmlFor="seg-active">Aktivní</label>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" role="switch" id="seg-inlist" checked={inList} onChange={(e) => setInList(e.target.checked)} />
            <label className="form-check-label" htmlFor="seg-inlist">Zobrazit ve sloupci Segmentace</label>
          </div>
        </div>
      </div>

      <div className="segment-rules-head d-flex align-items-center justify-content-between mb-2">
        <h6 className="text-muted text-uppercase fs-8 mb-0">Pravidla</h6>
        <Button variant="secondary" size="sm" onClick={addRule}><Plus size={14} aria-hidden="true" /> Přidat pravidlo</Button>
      </div>
      {rules.length === 0 ? (
        <p className="text-muted fs-8 mb-0">Zatím žádné pravidlo. Přidej první pomocí „+ Přidat pravidlo".</p>
      ) : (
        <div className="segment-rules-editor"><table>
          <thead>
            <tr><th>Metrika</th><th>Operátor</th><th>Hodnota</th><th style={{ width: 40 }}></th></tr>
          </thead>
          <tbody>
            {rules.map((rule, i) => (
              <tr key={i}>
                <td>
                  <Select label="Metrika" fieldClassName="rondo-segment-editor-select" value={rule.metric} onValueChange={(metric) => updateRule(i, { metric })} options={RULE_METRICS.map((m) => ({ value: m.code, label: m.label }))} />
                </td>
                <td>
                  <Select label="Operátor" fieldClassName="rondo-segment-editor-select" value={rule.operator} onValueChange={(operator) => updateRule(i, { operator })} options={RULE_OPERATORS.map((o) => ({ value: o.code, label: o.label }))} />
                </td>
                <td>
                  <input className="form-control form-control-sm" value={rule.value} onChange={(e) => updateRule(i, { value: e.target.value })} />
                </td>
                <td className="text-end">
                  <button className="btn btn-icon btn-sm btn-flush-dark" title="Smazat pravidlo" onClick={() => removeRule(i)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </ModalShell>
  );
}
